import React, { useEffect } from "react";
import { StyledFormElementsWrapper, ContainerGrid, FormLabel } from "../styles";
import FormField from "../../../common/FormField/FormField";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import AWSService from "../../../../services/AWSService";
import NoticeService from "../../../../services/NoticeService";
import {
  Grid,
  Radio,
  RadioGroup,
  makeStyles,
  FormControlLabel,
} from "@material-ui/core";
import styled from "styled-components";
import { SecondaryCTAButton } from "../../../common/Buttons";

const validFormat = /(?:image)/;

const useStyles = makeStyles((themes) => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
  },
}));

const HeaderFooter = ({
  values,
  handleChange,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  setFind,
  letterMenuItems,
  setLetterMenuItems,
  triggerNotify,
  setTriggerNotify,
  radioInputs,
}) => {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function s3Upload(file, field, setFieldValue) {
    if (file) {
      try {
        const errorMessage = "Invalid File Type";
        if (!validFormat.test(file?.type)) throw errorMessage;
        setLoader({ state: true, message: `Uploading file ${file.name} ...` });
        const payload = {
          key: file.name,
        };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
          enqueueSnackbar("Upload was successful", { variant: "success" });
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  useEffect(() => {
    if (values?.letterHeadName) {
      let result = letterMenuItems?.filter(
        (item) => item?.value === values?.letterHeadName
      )[0];
      if (result) {
        let metaData = JSON.parse(result?.headerFooterText);
        setFieldValue("headerImage", result?.headerImage);
        setFieldValue("footerImage", result?.footerImage);
        if (
          !JSON.parse(result?.headerFooterText) ||
          (!metaData.headerText?.length && !metaData.footerText?.length)
        ) {
          setFieldValue("headerText", [""]);
          setFieldValue("footerText", [""]);
        } else {
          setFieldValue("headerText", metaData?.headerText || [""]);
          setFieldValue("footerText", metaData?.footerText || [""]);
        }
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.letterHeadName]);

  useEffect(() => {
    async function getLetterHeadOption() {
      try {
        setLoader({ state: true, message: "fetching details..." });
        const response = await NoticeService.getLetterHeadOption();
        if (response) {
          setLetterMenuItems(
            response.map(
              ({
                letterHeadName,
                id,
                headerImage,
                footerImage,
                headerFooterText,
              }) => ({
                id: id,
                label: letterHeadName,
                value: letterHeadName,
                headerImage: headerImage,
                footerImage: footerImage,
                headerFooterText: headerFooterText,
              })
            )
          );
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
        setTriggerNotify(false);
      }
    }
    if (triggerNotify) {
      getLetterHeadOption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNotify]);

  const inputs = [
    {
      type: "autoComplete",
      name: "letterHeadName",
      label: "Letter Head Name",
      required: true,
    },
  ];

  const letter_head_images = [
    {
      name: "headerImage",
      label: "Header Image",
      type: "upload",
      accept: "image/*",
    },
    {
      name: "footerImage",
      label: "Footer Image",
      type: "upload",
      accept: "image/*",
    },
  ];

  const addMoreText = (fieldArray, setFieldValue, fieldName) => {
    setFieldValue(fieldName, [...fieldArray, ""]);
  };

  const handleDeleteText = (index, fieldArray, setFieldValue, fieldName) => {
    if (fieldArray?.length) {
      const filtered = fieldArray.filter((_, idx) => idx !== index);
      setFieldValue(fieldName, filtered);
    }
  };

  return (
    <StyledFormElementsWrapper style={{ marginBottom: 8, marginTop: 15 }}>
      <Grid
        className={classes.radioRoot}
        item
        xs={12}
        spacing={2}
        style={{ marginBottom: "5px" }}
      >
        <RadioGroup
          style={{
            flexDirection: "row",
            marginLeft: "10px",
          }}
          name={"typeOfLetterHead"}
          value={values.typeOfLetterHead}
          onChange={(e) => {
            setFieldValue("typeOfLetterHead", e.target.value);
          }}
        >
          {radioInputs.map((radio, key) => (
            <FormControlLabel
              {...{ key }}
              classes={{ label: classes.formLabel }}
              value={radio.value}
              control={<Radio size="medium" style={{ color: "#00838c" }} />}
              label={radio.label}
            />
          ))}
        </RadioGroup>
      </Grid>
      <ContainerGrid>
        {inputs.map((input, index) => {
          return (
            <FormField
              menuItems={letterMenuItems}
              key={index}
              {...{
                touched,
                errors,
                input,
                values,
                handleChange,
                handleBlur,
                setFieldValue,
                setFind,
              }}
            />
          );
        })}

        {values?.typeOfLetterHead === "image"
          ? letter_head_images.map((image, index) => (
              <div style={{ marginTop: 10 }}>
                <FormLabel>{image.label}</FormLabel>
                <CustomFileUpload
                  error={touched[image.name] && errors[image.name]}
                  setFile={(file) => s3Upload(file, image.name, setFieldValue)}
                  style={{
                    width: "100%",
                  }}
                  accept={image.accept}
                  file={{ name: getFileName(values[image.name]) }}
                />
              </div>
            ))
          : null}
      </ContainerGrid>
      {values?.typeOfLetterHead === "text" ? (
        <ContainerGrid>
          <div style={{ marginTop: 10 }}>
            <FormLabel>Header Text</FormLabel>
            {values.headerText.map((itemText, index) => (
              <FileContainer>
                <TextFileontainer className="input-text">
                  <CustomInputField
                    variant="outlined"
                    style={{ width: "100%", marginTop: 6 }}
                    name={`headerText[${index}]`}
                    value={itemText}
                    onChange={handleChange}
                  />
                  {values.headerText.length > 1 && (
                    <DeleteIcon
                      onClick={() =>
                        handleDeleteText(
                          index,
                          values.headerText,
                          setFieldValue,
                          "headerText"
                        )
                      }
                      key={index}
                    >
                      {" "}
                      <img
                        src={require("../../../../assets/images/removeIcon.svg")}
                        alt="delete"
                      ></img>
                    </DeleteIcon>
                  )}
                </TextFileontainer>
              </FileContainer>
            ))}

            <SecondaryCTAButton
              style={{ marginTop: "30px", float: "left" }}
              onClick={() => {
                addMoreText(values.headerText, setFieldValue, "headerText");
              }}
            >
              Add next line header text
            </SecondaryCTAButton>
          </div>
          <div style={{ marginTop: 10 }}>
            <FormLabel>Footer Text</FormLabel>
            {values.footerText.map((itemText, index) => (
              <FileContainer>
                <TextFileontainer className="input-text">
                  <CustomInputField
                    variant="outlined"
                    style={{ width: "100%", marginTop: 6 }}
                    name={`footerText[${index}]`}
                    value={itemText}
                    onChange={handleChange}
                  />
                  {values.footerText.length > 1 && (
                    <DeleteIcon
                      onClick={() =>
                        handleDeleteText(
                          index,
                          values.footerText,
                          setFieldValue,
                          "footerText"
                        )
                      }
                      key={index}
                    >
                      {" "}
                      <img
                        src={require("../../../../assets/images/removeIcon.svg")}
                        alt="delete"
                      ></img>
                    </DeleteIcon>
                  )}
                </TextFileontainer>
              </FileContainer>
            ))}

            <SecondaryCTAButton
              style={{ marginTop: "30px", float: "left" }}
              onClick={() => {
                addMoreText(values.footerText, setFieldValue, "footerText");
              }}
            >
              Add next line footer text
            </SecondaryCTAButton>
          </div>
        </ContainerGrid>
      ) : null}
    </StyledFormElementsWrapper>
  );
};

export default HeaderFooter;

const FileContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TextFileontainer = styled.div`
  display: flex;
`;

const DeleteIcon = styled.div`
  margin: 20px 5px 0px;
  cursor: pointer;
`;
